import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import BarcodeScanner from "../components/BarcodeScanner";
import {
  ArrowDownToLine,
  Loader2,
  Package,
  Plus,
  Trash2,
  CheckCircle,
  Barcode,
  ShoppingCart,
  User,
  Users,
  Search,
  X
} from "lucide-react";

const MOTIVOS_ENTRADA = [
  { value: "compra", label: "Compra" },
  { value: "devolucion", label: "Devolución de cliente" },
  { value: "ajuste_positivo", label: "Ajuste positivo" },
  { value: "transferencia", label: "Transferencia entrada" },
  { value: "produccion", label: "Producción" },
  { value: "donacion", label: "Donación recibida" },
  { value: "otro", label: "Otro" }
];

export default function EntradasInventarioPage() {
  const [articulos, setArticulos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedArticulo, setSelectedArticulo] = useState(null);
  const [articuloSearch, setArticuloSearch] = useState("");
  const [showArticulosList, setShowArticulosList] = useState(false);
  const [items, setItems] = useState([]);
  const [referenciaMode, setReferenciaMode] = useState("usuario"); // "usuario" or "manual"
  const [formData, setFormData] = useState({
    cantidad: 1,
    motivo: "compra",
    referencia: "",
    referencia_usuario_id: "",
    observaciones: "",
    precio_unitario: 0
  });
  const { getAuthHeaders } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [articulosRes, usuariosRes] = await Promise.all([
        axios.get(`${API}/inventario/articulos?limit=1000`, getAuthHeaders()),
        axios.get(`${API}/users`, getAuthHeaders())
      ]);
      // Handle paginated response format
      const articulosData = articulosRes.data.items || articulosRes.data;
      setArticulos(Array.isArray(articulosData) ? articulosData.filter(a => a.activo !== false) : []);
      setUsuarios(usuariosRes.data || []);
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBarcodeScanned = async (codigo) => {
    try {
      const response = await axios.get(`${API}/inventario/articulos/codigo/${codigo}`, getAuthHeaders());
      if (response.data) {
        setSelectedArticulo(response.data);
        setFormData(prev => ({ ...prev, precio_unitario: response.data.precio_costo || 0 }));
        toast.success(`Artículo encontrado: ${response.data.nombre}`);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error("Artículo no encontrado con ese código");
      }
    }
  };

  const handleSelectArticulo = (articuloId) => {
    const articulo = articulos.find(a => a.id === articuloId);
    if (articulo) {
      setSelectedArticulo(articulo);
      setFormData(prev => ({ ...prev, precio_unitario: articulo.precio_costo || 0 }));
      setArticuloSearch("");
      setShowArticulosList(false);
    }
  };

  // Filter articles based on search
  const filteredArticulos = articulos.filter(art => {
    if (!articuloSearch.trim()) return true;
    const search = articuloSearch.toLowerCase();
    return (
      art.codigo?.toLowerCase().includes(search) ||
      art.nombre?.toLowerCase().includes(search)
    );
  });

  const addToList = () => {
    if (!selectedArticulo) {
      toast.error("Selecciona un artículo");
      return;
    }
    if (formData.cantidad <= 0) {
      toast.error("La cantidad debe ser mayor a 0");
      return;
    }

    const existingIndex = items.findIndex(i => i.articulo_id === selectedArticulo.id);
    if (existingIndex >= 0) {
      // Update existing item
      const newItems = [...items];
      newItems[existingIndex].cantidad += formData.cantidad;
      setItems(newItems);
    } else {
      // Add new item
      setItems([...items, {
        articulo_id: selectedArticulo.id,
        articulo: selectedArticulo,
        tipo: "entrada",
        cantidad: formData.cantidad,
        motivo: formData.motivo,
        referencia: formData.referencia,
        observaciones: formData.observaciones,
        precio_unitario: formData.precio_unitario
      }]);
    }

    // Reset form
    setSelectedArticulo(null);
    setFormData({
      cantidad: 1,
      motivo: formData.motivo, // Keep same motivo for batch entries
      referencia: formData.referencia, // Keep reference
      observaciones: "",
      precio_unitario: 0
    });
  };

  const removeFromList = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (items.length === 0) {
      toast.error("Agrega al menos un artículo");
      return;
    }

    try {
      setSaving(true);
      // Clean items to only include fields expected by backend
      const cleanItems = items.map(item => ({
        articulo_id: item.articulo_id,
        tipo: item.tipo,
        cantidad: item.cantidad,
        motivo: item.motivo,
        referencia: item.referencia || "",
        observaciones: item.observaciones || "",
        precio_unitario: item.precio_unitario || 0
      }));
      
      const response = await axios.post(`${API}/inventario/movimientos/lote`, cleanItems, getAuthHeaders());
      
      if (response.data.errores > 0) {
        toast.warning(`${response.data.procesados} entradas registradas, ${response.data.errores} errores`);
        if (response.data.detalles_errores?.length > 0) {
          response.data.detalles_errores.forEach(err => {
            toast.error(`Error: ${err.error}`);
          });
        }
      } else {
        toast.success(`${response.data.procesados} entradas registradas exitosamente`);
      }
      
      setItems([]);
      fetchData();
    } catch (err) {
      console.error("Error al registrar entradas:", err);
      toast.error(err.response?.data?.detail || "Error al registrar entradas");
    } finally {
      setSaving(false);
    }
  };

  const totalItems = items.reduce((sum, i) => sum + i.cantidad, 0);
  const totalValue = items.reduce((sum, i) => sum + (i.cantidad * i.precio_unitario), 0);

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="entradas-inventario-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
              <ArrowDownToLine className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
                Entradas de Inventario
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Registrar ingreso de artículos al inventario
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form Section */}
          <div className="lg:col-span-2 space-y-6">
            {/* Barcode Scanner */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Barcode className="w-5 h-5" />
                  Escanear Artículo
                </CardTitle>
                <CardDescription>
                  Escanea el código de barras o selecciona manualmente
                </CardDescription>
              </CardHeader>
              <CardContent>
                <BarcodeScanner onScan={handleBarcodeScanned} />
              </CardContent>
            </Card>

            {/* Manual Selection & Details */}
            <Card>
              <CardHeader>
                <CardTitle>Datos de Entrada</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label>Artículo</Label>
                    <div className="relative">
                      <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                          value={selectedArticulo ? `${selectedArticulo.codigo} - ${selectedArticulo.nombre}` : articuloSearch}
                          onChange={(e) => {
                            setArticuloSearch(e.target.value);
                            setSelectedArticulo(null);
                            setShowArticulosList(true);
                          }}
                          onFocus={() => setShowArticulosList(true)}
                          placeholder="Buscar por código o nombre..."
                          className="pl-10 pr-10"
                        />
                        {(selectedArticulo || articuloSearch) && (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedArticulo(null);
                              setArticuloSearch("");
                              setShowArticulosList(false);
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      
                      {showArticulosList && !selectedArticulo && (
                        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {filteredArticulos.length === 0 ? (
                            <div className="p-3 text-center text-slate-500">No se encontraron artículos</div>
                          ) : (
                            filteredArticulos.slice(0, 50).map(art => (
                              <button
                                key={art.id}
                                type="button"
                                onClick={() => handleSelectArticulo(art.id)}
                                className="w-full px-3 py-2 text-left hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 border-b border-slate-100 dark:border-slate-700 last:border-0"
                              >
                                <span className="font-mono text-xs text-slate-500 bg-slate-100 dark:bg-slate-600 px-2 py-0.5 rounded">{art.codigo}</span>
                                <span className="flex-1 truncate">{art.nombre}</span>
                                <span className="text-xs text-slate-400">{art.stock_actual} {art.unidad_medida}</span>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    {selectedArticulo && (
                      <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex justify-between">
                          <div>
                            <p className="font-medium text-green-800">{selectedArticulo.nombre}</p>
                            <p className="text-sm text-green-600 font-mono">{selectedArticulo.codigo}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-green-600">Stock actual</p>
                            <p className="font-bold text-green-800">{selectedArticulo.stock_actual} {selectedArticulo.unidad_medida}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Cantidad *</Label>
                    <Input
                      type="number"
                      value={formData.cantidad}
                      onChange={(e) => setFormData({ ...formData, cantidad: parseFloat(e.target.value) || 0 })}
                      min={0.01}
                      step={1}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Precio Unitario ($)</Label>
                    <Input
                      type="number"
                      value={formData.precio_unitario}
                      onChange={(e) => setFormData({ ...formData, precio_unitario: parseFloat(e.target.value) || 0 })}
                      min={0}
                      step={0.01}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Motivo</Label>
                    <Select value={formData.motivo} onValueChange={(v) => setFormData({ ...formData, motivo: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MOTIVOS_ENTRADA.map(m => (
                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center justify-between">
                      <span>Proveedor / Referencia</span>
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          variant={referenciaMode === "usuario" ? "default" : "ghost"}
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() => setReferenciaMode("usuario")}
                        >
                          <Users className="w-3 h-3 mr-1" />
                          Usuario
                        </Button>
                        <Button
                          type="button"
                          variant={referenciaMode === "manual" ? "default" : "ghost"}
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() => setReferenciaMode("manual")}
                        >
                          Manual
                        </Button>
                      </div>
                    </Label>
                    {referenciaMode === "usuario" ? (
                      <Select 
                        value={formData.referencia_usuario_id || "none"} 
                        onValueChange={(v) => {
                          const selectedValue = v === "none" ? "" : v;
                          const usuario = usuarios.find(u => u.id === selectedValue);
                          setFormData({ 
                            ...formData, 
                            referencia_usuario_id: selectedValue,
                            referencia: usuario ? usuario.name : ""
                          });
                        }}
                      >
                        <SelectTrigger>
                          <User className="w-4 h-4 mr-2 text-slate-400" />
                          <SelectValue placeholder="Seleccionar usuario..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin asignar</SelectItem>
                          {usuarios.map(u => (
                            <SelectItem key={u.id} value={u.id}>
                              <div className="flex items-center gap-2">
                                <span>{u.name}</span>
                                <span className="text-xs text-slate-400">{u.email}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        value={formData.referencia}
                        onChange={(e) => setFormData({ ...formData, referencia: e.target.value, referencia_usuario_id: "" })}
                        placeholder="Nombre del proveedor o # factura"
                      />
                    )}
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Observaciones</Label>
                    <Textarea
                      value={formData.observaciones}
                      onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                      placeholder="Notas adicionales..."
                      rows={2}
                    />
                  </div>
                </div>
                <Button 
                  onClick={addToList} 
                  className="w-full bg-green-600 hover:bg-green-700"
                  disabled={!selectedArticulo || formData.cantidad <= 0}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar a la Lista
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Items List */}
          <div className="space-y-6">
            <Card className="border-green-200">
              <CardHeader className="bg-green-50 rounded-t-lg">
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5" />
                    Lista de Entradas
                  </span>
                  <Badge variant="secondary">{items.length} items</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                {items.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Package className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                    <p>Agrega artículos a la lista</p>
                  </div>
                ) : (
                  <ScrollArea className="h-64">
                    <div className="space-y-2">
                      {items.map((item, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{item.articulo?.nombre}</p>
                            <p className="text-xs text-slate-500 font-mono">{item.articulo?.codigo}</p>
                          </div>
                          <div className="text-right mr-2">
                            <Badge className="bg-green-100 text-green-700">+{item.cantidad}</Badge>
                          </div>
                          <Button variant="ghost" size="icon" onClick={() => removeFromList(index)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}

                {items.length > 0 && (
                  <div className="mt-4 pt-4 border-t space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Total unidades:</span>
                      <span className="font-bold">{totalItems}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Valor total:</span>
                      <span className="font-bold text-green-600">
                        ${totalValue.toLocaleString("es-PA", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <Button 
                      onClick={handleSubmit} 
                      className="w-full bg-green-600 hover:bg-green-700"
                      disabled={saving}
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4 mr-2" />
                      )}
                      Registrar {items.length} Entrada{items.length > 1 ? "s" : ""}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
